import { useEffect, useId, useRef, useState } from 'react'
import { isSearchableQuery } from '../lib/search'
import { useT } from '../lib/i18n'
import { useSearchStore } from '../stores/searchStore'
import { IconButton } from './IconButton'
import { CloseIcon, SearchIcon } from './Icons'

/**
 * How long a pause in typing becomes a query. The store never debounces — it answers what it is
 * asked — so the timing lives here, with the field the reader is typing into.
 */
const DEBOUNCE_MS = 250

type SearchFieldProps = {
  /** The chat the thread's field searches, or `null` for every chat the caller is in (the list's). */
  chatId: string | null
  /** The field's accessible name: the list's says "Search messages", the thread's "Search in this chat". */
  label: string
  /** Escape and Clear leave search mode; the pane decides what that means for its own body. */
  onClose: () => void
}

/**
 * The search field both panes wear (W3). One control, two scopes: the frozen contract says the chat
 * list's search and the thread's are the same endpoint, and `chatId` is the only thing that differs.
 *
 * The query is sent as typed, trimmed — the api matches a literal substring and treats `%`, `_` and
 * `\` as characters, so nothing is escaped here (docs/CONTRACT.md §4 *Search*).
 */
export function SearchField({ chatId, label, onClose }: SearchFieldProps) {
  const t = useT()
  const run = useSearchStore((state) => state.run)
  const clear = useSearchStore((state) => state.clear)
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const fieldId = useId()

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Results belong to the field: the moment it goes away, so do they. Every way out of search mode
  // unmounts this component, so this is the one place that has to remember.
  useEffect(() => () => clear(), [clear])

  // Escape leaves search mode wherever the focus is — the app's one rule for a panel that is up
  // (docs/DESIGN.md §6.5) — not only while the field itself holds the caret.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      clear()
      onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [clear, onClose])

  useEffect(() => {
    // Text that is not a query is not one: the results go at once rather than lingering under a
    // field that no longer holds them. Anything else waits for a pause — one request per typed
    // query, not one per keystroke.
    if (!isSearchableQuery(value)) {
      clear()
      return
    }
    const timer = window.setTimeout(() => void run(value, chatId), DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [value, chatId, run, clear])

  const close = () => {
    clear()
    onClose()
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1">
      <label htmlFor={fieldId} className="sr-only">
        {label}
      </label>
      <span className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-well bg-surface2 px-3 ring-1 ring-line ring-inset transition-colors duration-quick focus-within:ring-accent/70">
        <SearchIcon size={18} className="shrink-0 text-muted" />
        <input
          id={fieldId}
          ref={inputRef}
          type="search"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder={t('search.placeholder')}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          className="min-h-11 min-w-0 flex-1 bg-transparent py-2 text-ui text-ink placeholder:text-muted"
        />
      </span>
      <IconButton label={value.trim() ? t('search.clear') : t('search.close')} size={40} onClick={close}>
        <CloseIcon size={20} />
      </IconButton>
    </div>
  )
}
